/**
 * Project template contracts (MP-07).
 *
 * A template is a `Project` row with `isTemplate = true` — the reusable half
 * of a job (CDM roles, the professional team, client contacts, the CPP
 * narrative, trade tasks and the COSHH selection). See
 * `lib/projects/template-fields.ts` for the exact column split.
 *
 * Wire surfaces:
 *   GET    /api/mobile/v1/templates              → TemplateListResponse
 *   POST   /api/mobile/v1/templates/:id/use      → TemplateUseResponse
 *   DELETE /api/mobile/v1/templates/:id          → { ok: true }
 *
 * Mirrors the web routes at `/api/templates/*`, which return the same slim
 * summary shape (`lib/projects/templates.ts` → `TEMPLATE_SUMMARY_SELECT`).
 *
 * The summary is deliberately slim: instantiation is SERVER-side, so a client
 * never needs the full field set to start a job from a template — only enough
 * to tell two templates apart in a picker.
 */
import { z } from "zod";
import { projectInductionTextField } from "../project-induction-limits";
import { entityIdSchema } from "../common";
import { ContractorRoleSchema, ProjectSchema } from "./project";

// Response objects strip unknown additive keys, like the canonical ProjectSchema.
// Request strictness remains independent below.
export const TemplateSummarySchema = z.object({
  id: entityIdSchema,
  title: z.string(),
  scopeDescription: z.string().nullable(),
  contractorRole: ContractorRoleSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TemplateSummary = z.infer<typeof TemplateSummarySchema>;

/**
 * `canUseTemplates` is the PLAN entitlement (`FEATURE.PROJECT_CLONING`), not
 * the listing gate. Listing and deleting are role-gated only, so an account
 * that downgrades to Starter still sees and can delete templates it owns —
 * the flag exists so a "Start from a template" picker hides itself rather
 * than offering a workflow whose POST can only 403. Web parity: see
 * `app/api/templates/route.ts`.
 */
export const TemplateListResponseSchema = z.object({
  templates: z.array(TemplateSummarySchema),
  canUseTemplates: z.boolean(),
});

export type TemplateListResponse = z.infer<typeof TemplateListResponseSchema>;

export const TemplateUseRequestSchema = z
  .object({
    title: z.string().trim().min(1, "Project title is required").pipe(projectInductionTextField("title")),
    /**
     * REQUIRED, and deliberately never defaulted to the template's stored
     * address: a template exists to be reused at a DIFFERENT site, so
     * inheriting the previous address would silently make the new job claim
     * the wrong location — and the nearest A&E derived from it would reach
     * the Emergency Plan unnoticed.
     */
    siteAddress: z.string().trim().min(1, "Site address is required").pipe(projectInductionTextField("siteAddress")),
    /**
     * The SavedSite this job is at, when the user picked one.
     *
     * Site-level supervisor assignments grant access to present AND future
     * projects by `savedSiteId`, so a job created at a saved site must carry
     * the link or the supervisor assigned there never sees it — the identical
     * rule `ProjectCreateRequest.savedSiteId` carries for manual creation.
     * Without this field the template flow could only ever record the address
     * text, silently producing an unscoped job at a scoped site.
     *
     * Tenant-validated server-side under a SavedSite row lock, and refused
     * when the site's address does not match `siteAddress` above. "" and null
     * both normalise to "no link" — never an FK value.
     */
    savedSiteId: z
      .string()
      .nullable()
      .transform((v) => (v === "" ? null : v))
      // `.optional()` OUTERMOST so the inferred key stays optional: a
      // transform inside it makes the key REQUIRED in the output type, which
      // would force every caller to send the field — the one thing an
      // installed app talking to an older API must not do.
      .optional(),
    /**
     * The CDM role chosen a step earlier in the new-project funnel. That
     * fresh choice wins over the role stored on the template.
     */
    contractorRole: ContractorRoleSchema.optional(),
  })
  .strict();

export type TemplateUseRequest = z.infer<typeof TemplateUseRequestSchema>;

/** The created project — a real job, `isTemplate: false`. */
export const TemplateUseResponseSchema = z.object({ project: ProjectSchema });

export type TemplateUseResponse = z.infer<typeof TemplateUseResponseSchema>;

// No delete-response schema: DELETE answers `{ ok: true }` and the client
// returns void (same as `ProjectsApi.archive`). An exported schema nothing
// parses is a contract with no consumer — add one when a caller needs it.
